<template>
  <div
    :class="[googleAdContainer, googleAdHeader, googleAdInline, googleAdCatsplash, dynamicAdClasses, googelAdPDPBottom]"
  >
    <div
      v-if="isHomepage"
      :id="adId"
    />
    <div
      v-if="isDiscovery && !isDiscoveryAdDisabled"
      :id="adId"
      :aria-label="isDiscovery && !isInline ? 'google Display Ad' : ''"
    />
    <div
      v-if="isProduct"
      :id="adId"
      :class="googleDisplayAd"
      role="complementary"
      aria-label="Google display ad"
    />
    <div
      v-if="showSponsoredLabel"
      class="google-ad-sponsored-label"
    >
      <span>
        Sponsored
      </span>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  watch,
  computed,
  onMounted,
} from 'vue';
import { useStore } from 'vuex';
import useGoogleAdClasses from '../composables/useGoogleAdClasses';
import L1Categories from '../utils/L1CategoryPages';
import { isSponsoredBrandExperimentEnabled } from '../utils/experimentUtil';

const props = defineProps({
  adId: {
    type: String,
    required: true,
    default: '',
  },
  page: {
    type: String,
    required: true,
    default: '',
  },
  device: {
    type: String,
    required: true,
    default: '',
  },
  brand: {
    type: String,
    required: false,
    default: '',
  },
  envBrand: {
    type: String,
    required: false,
    default: 'mcom',
  },
  isUSARegion: {
    type: Boolean,
    required: false,
    default: false,
  },
  isWeddingRegistry: {
    type: Boolean,
    required: false,
    default: false,
  },
  categoryId: {
    type: String,
    required: false,
    default: '',
  },
  customStyleClass: {
    type: String,
    required: false,
    default: '',
  },
});
const emit = defineEmits(['googleAdLoaded']);
const store = useStore();
const isHomepage = props.page === 'homepage';

const isDiscovery = props.page === 'BROWSE' || props.page === 'SEARCH' || props.page === 'SPLASH' || props.page === 'SUB_SPLASH';
const isInline = props.adId.indexOf('Inline') !== -1;
const isCatsplash = props.page === 'SPLASH' || props.adId === 'GOOGLE_AD_3';
const isHeader = isDiscovery && !isInline && !isCatsplash;
const isProduct = props.page === 'product';
const isBrowse = props.page === 'BROWSE';
const isProductBottomAd = props.page === 'product' && props.adId === 'googleAdOnPdpBottom' && props.device === 'desktop' && props.envBrand === 'mcom';
const isRescrictedBrand = props.brand === 'CHANEL' || props.brand === 'HERMÈS' || props.brand === 'FINISHLINE';
const isL1CatsplashPage = L1Categories.L1CategoryList[props.envBrand].categoryIds.indexOf(props.categoryId) !== -1 || false;

const catsplashRestrictedBrand = isCatsplash && isRescrictedBrand;
const deviceRestriction = (props.page === 'SEARCH' || props.page === 'BROWSE') && props.device === 'mobile' && props.envBrand === 'mcom' && !isInline;
let sponsoredBrandRestriction = false;
if ((props.page === 'BROWSE' || props.page === 'SEARCH') && (props.adId === 'googleAdOnSearch' || props.adId === 'GOOGLE_AD_3') && !isL1CatsplashPage && isSponsoredBrandExperimentEnabled()) {
  sponsoredBrandRestriction = true;
}
const isDiscoveryAdDisabled = deviceRestriction || catsplashRestrictedBrand || sponsoredBrandRestriction || props.isWeddingRegistry;

const {
  googleAdContainer,
  googleDisplayAd,
  googleAdHeader,
  googleAdInline,
  googleAdCatsplash,
  dynamicAdClasses,
  googelAdPDPBottom,
} = useGoogleAdClasses(props.adId, props.envBrand, props.device, isHeader, isInline, isCatsplash, isProductBottomAd, isBrowse);

const isAdLoaded = computed(() => store.getters['googleAdStore/getAdById'](props.adId)?.isLoaded);
const showSponsoredLabel = ref(false);
watch(isAdLoaded, (isLoaded, prevIsLoaded) => {
  const isFacetSelection = prevIsLoaded && !isLoaded;
  if (isLoaded) {
    emit('googleAdLoaded');
    showSponsoredLabel.value = isLoaded && props.envBrand !== 'bcom';
    dynamicAdClasses.value = props.adId !== 'GOOGLE_AD_3' ? props.customStyleClass : '';
  }
  if (isFacetSelection) {
    showSponsoredLabel.value = false;
    dynamicAdClasses.value = '';
  }
});
onMounted(() => {
  if (props.page !== 'homepage') store.dispatch('googleAdStore/adMounted', props.adId);
});
</script>

<style lang="scss">
.google-ad-container {
  display: grid;
  width: 100%;
}

.google-ad-container.google-ad-pdp-bottom{
  justify-content: center;
}
.google-ad-header-mcom {
  #googleAdOnSearch iframe {
    @include breakpoint(medium up) {
      margin-top: rem-calc(40px);
    }
  }
  .sLabel {
    @include breakpoint(medium up) {
      margin-bottom: rem-calc(10px);
    }
  }
}
div.google-ad-header-mcom:has(> .google-ad-sponsored-label) {
    @include breakpoint(medium up) {
      margin-bottom: rem-calc(10px);
    }
}

div.google-ad-browse-header-mcom:has(> .google-ad-sponsored-label) {
    @include breakpoint(medium up) {
      margin-bottom: rem-calc(48px);
    }
}

.google-ad-header-bcom {
  #googleAdOnSearch iframe {
    @include breakpoint(large) {
      margin-top: rem-calc(40px);
      margin-bottom: rem-calc(18px);
    }
    @include breakpoint (medium down) {
      margin-top: rem-calc(16px);
      margin-bottom: rem-calc(32px);
    }
  }
}
.google-ad-inline-mcom {
  padding-left: rem-calc(96);
  padding-right: rem-calc(96);
  .sLabel {
    @include breakpoint(large) {
      margin-bottom: rem-calc(48px);
    }
    @include breakpoint(medium down) {
      margin-bottom: rem-calc(32px);
    }
  }
}
div.google-ad-inline-mcom:has(> .google-ad-sponsored-label) {
  @include breakpoint(large) {
      margin-bottom: rem-calc(48px);
    }
    @include breakpoint(medium down) {
      margin-bottom: rem-calc(32px);
    }
}
.google-ad-inline-bcom {
  #googleAdOnSearchInline_3, #googleAdOnSearchInline_6 {
    iframe {
      @include breakpoint(large) {
        padding-left: rem-calc(96);
        padding-right: rem-calc(96);
        margin-bottom: rem-calc(48px);
      }
      @media screen and (min-width: 600px) and (max-width: 1023px) {
        margin-bottom: rem-calc(32px);
      }
      @include breakpoint(small) {
        margin-bottom: rem-calc(48px);
      }
    }
  }
}
.google-ad-inline-mobile-mcom {
  padding-left: rem-calc(8);
  padding-right: rem-calc(8);
  .sLabel {
    margin-bottom: rem-calc(32);
  }
}
.google-ad-inline-mobile-mcom:has(> .google-ad-sponsored-label) {
  margin-bottom: rem-calc(32);
}
.google-ad-inline-mobile-bcom {
  #googleAdOnSearchMobileInline_3, #googleAdOnSearchMobileInline_6 {
    iframe {
      padding-left: rem-calc(8);
      padding-right: rem-calc(8);
      margin-bottom: rem-calc(32);
    }
  }
}
.google-ad-catsplash {
    #googleAdOnCatsplash iframe {
    @include breakpoint(large) {
      margin-top: rem-calc(24px);
    }
    @media screen and (min-width: 600px) and (max-width: 1023px) {
      margin-top: rem-calc(22px);
    }
    @include breakpoint(small) {
      margin-top: rem-calc(24px);
    }
  }
}
.google-ad-container-catsplash {
  height: auto;
  @include breakpoint(large) {
    width: rem-calc(954px);
  }
  @include breakpoint(small) {
    width: rem-calc(335px);
  }
}
.google-ad-container-catsplash:has(> .google-ad-sponsored-label) {
  @include breakpoint(large) {
    margin-bottom: rem-calc(64px);
  }
  @include breakpoint(medium down) {
    margin-bottom: rem-calc(48px);
  }
}
.google-display-ad:empty {
  padding: 0;
  margin: 0;
}
//third-party code(google ad) is injected into this component. CSS needs to be unscoped to apply styling.
.google-ad-sponsored-label {
  margin-left: auto;
  float: right;
  line-height: 0.75rem;
  padding-top: 0.125rem;
  padding-bottom: 0.125rem;
  bottom: 2.625rem;
  right: 0;
  font-size: 0.625rem;
  font-weight: 700;
  color: $gray-2-color;
  position: static;
}
.google-ad-placeholder-media {
  display: flex;
  justify-content: center;
}
</style>
